// ============================================================
// PROFILE STRENGTH SIDEBAR COMPONENT
// 
// Unified sidebar for ClientProfilePage that shows:
// - Single "Profile Strength" metric (replaces both readiness & completion)
// - Visual ring/progress indicator
// - Missing fields list with priority
// - Quick stats summary
// 
// Usage: Replace the existing two-metric sidebar with this component
// ============================================================

import React, { useState, useEffect } from 'react';
import { 
  AlertCircle, 
  CheckCircle, 
  ChevronDown, 
  ChevronUp,
  TrendingUp,
  Building2,
  Bed,
  Users,
  MapPin
} from 'lucide-react';
// THE strength scale — shared with the EA personalization widget. Do not add a second one.
import { getStrengthLevel, getStrengthHex } from '@/lib/strengthScale';

import { API_BASE, apiFetch } from '../../config/apiBase';
// ============================================================
// DVA DESIGN SYSTEM COLORS
// ============================================================
const DVA_COLORS = {
  navy: '#18365E',
  orange: '#FF6E4C',
  orangeLight: '#FFF4F1',
  slate: '#717182',
  slateLight: '#F5F7FA',
  border: '#E5E7EB',
  success: '#10B981',
  successLight: '#ECFDF5',
  warning: '#F59E0B',
  warningLight: '#FFFBEB',
  error: '#EF4444',
  errorLight: '#FEF2F2',
};

// ============================================================
// CIRCULAR PROGRESS RING
// ============================================================
function ProgressRing({ percentage, size = 120, strokeWidth = 10, color }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={DVA_COLORS.border}
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color || DVA_COLORS.orange}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.5s ease-in-out' }}
        />
      </svg>
      {/* Center text */}
      <div 
        className="absolute inset-0 flex flex-col items-center justify-center"
      >
        <span 
          className="text-3xl font-bold" 
          style={{ color: DVA_COLORS.navy }}
        >
          {percentage}%
        </span>
      </div>
    </div>
  );
}

// ============================================================
// PROFILE STRENGTH SIDEBAR
// ============================================================
export default function ProfileStrengthSidebar({ 
  profile, 
  strength,
  isLoading = false,
  onMissingFieldClick = null  // ✅ NEW: Callback when user clicks a missing field
}) {
  const [showAllMissing, setShowAllMissing] = useState(false);

  // Fallback if strength not provided - calculate locally
  const strengthData = strength || calculateProfileStrength(profile);

  // Colour resolves through the one shared scale, so the ring can never disagree with the label.
  const scoreColor = strengthData.color || getStrengthHex(strengthData.score || 0);

  // Missing fields to display
  const missingFields = strengthData.missingFields || [];
  const displayedMissing = showAllMissing ? missingFields : missingFields.slice(0, 3);

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl border p-6" style={{ borderColor: DVA_COLORS.border }}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="h-24 bg-gray-200 rounded-full w-24 mx-auto mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ============================================================ */}
      {/* PROFILE STRENGTH CARD */}
      {/* ============================================================ */}
      <div 
        className="bg-white rounded-2xl border p-6"
        style={{ borderColor: DVA_COLORS.border }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold" style={{ color: DVA_COLORS.navy }}>
            Profile Strength
          </h3>
          <span 
            className="text-xs font-medium px-2 py-1 rounded-full"
            style={{ 
              backgroundColor: scoreColor + '20',
              color: scoreColor 
            }}
          >
            {strengthData.label}
          </span>
        </div>

        {/* Progress Ring */}
        <div className="flex justify-center mb-4">
          <ProgressRing 
            percentage={strengthData.score || 0} 
            color={scoreColor}
          />
        </div>

        {/* Score Breakdown */}
        <div className="space-y-2 mb-4">
          <ScoreBreakdownRow 
            label="Critical fields" 
            score={strengthData.breakdown?.critical || 0}
            weight="60%"
          />
          <ScoreBreakdownRow 
            label="Optional fields" 
            score={strengthData.breakdown?.optional || 0}
            weight="30%"
          />
          <ScoreBreakdownRow 
            label="Data quality" 
            score={strengthData.breakdown?.quality || 0}
            weight="10%"
          />
        </div>

        {/* Divider */}
        <div className="border-t my-4" style={{ borderColor: DVA_COLORS.border }}></div>

        {/* Missing Fields */}
        {missingFields.length > 0 ? (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4" style={{ color: DVA_COLORS.orange }} />
              <span className="text-xs font-medium" style={{ color: DVA_COLORS.navy }}>
                Missing fields ({missingFields.length})
              </span>
            </div>
            <ul className="space-y-1">
              {displayedMissing.map((field, idx) => (
                <li key={idx}>
                  {onMissingFieldClick ? (
                    <button
                      type="button"
                      onClick={() => onMissingFieldClick(field)}
                      className="text-xs flex items-center gap-2 hover:underline cursor-pointer text-left"
                      style={{ color: DVA_COLORS.orange }}
                    >
                      <span className="w-1 h-1 rounded-full bg-current flex-shrink-0"></span>
                      {field}
                    </button>
                  ) : (
                    <span
                      className="text-xs flex items-center gap-2"
                      style={{ color: DVA_COLORS.orange }}
                    >
                      <span className="w-1 h-1 rounded-full bg-current"></span>
                      {field}
                    </span>
                  )}
                </li>
              ))}
            </ul>
            {missingFields.length > 3 && (
              <button
                onClick={() => setShowAllMissing(!showAllMissing)}
                className="mt-2 text-xs font-medium flex items-center gap-1"
                style={{ color: DVA_COLORS.slate }}
              >
                {showAllMissing ? (
                  <>Show less <ChevronUp className="w-3 h-3" /></>
                ) : (
                  <>Show {missingFields.length - 3} more <ChevronDown className="w-3 h-3" /></>
                )}
              </button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4" style={{ color: DVA_COLORS.success }} />
            <span className="text-xs" style={{ color: DVA_COLORS.success }}>
              All critical fields complete!
            </span>
          </div>
        )}
      </div>

      {/* ============================================================ */}
      {/* QUICK STATS CARD */}
      {/* ============================================================ */}
      <div 
        className="bg-white rounded-2xl border p-6"
        style={{ borderColor: DVA_COLORS.border }}
      >
        <h3 className="text-sm font-semibold mb-4" style={{ color: DVA_COLORS.navy }}>
          Profile Summary
        </h3>

        <div className="space-y-3">
          {/* Organization */}
          <QuickStat 
            icon={Building2}
            label="Organization"
            value={profile?.client_name || profile?.organization_name || '—'}
          />

          {/* Type */}
          <QuickStat 
            icon={TrendingUp}
            label="Type"
            value={profile?.organization_type || '—'}
          />

          {/* Beds */}
          <QuickStat 
            icon={Bed}
            label="Beds"
            value={profile?.beds || profile?.total_beds ? Number(profile?.beds || profile?.total_beds).toLocaleString() : '—'}
          />

          {/* VBC Lives */}
          <QuickStat 
            icon={Users}
            label="VBC Lives"
            value={profile?.lives_under_vbc_agreements || profile?.lives_under_vbc ? Number(profile?.lives_under_vbc_agreements || profile?.lives_under_vbc).toLocaleString() : '—'}
          />
        </div>
      </div>
    </div>
  );
}

// ============================================================
// SCORE BREAKDOWN ROW
// ============================================================
function ScoreBreakdownRow({ label, score, weight }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="text-xs" style={{ color: DVA_COLORS.slate }}>
          {label}
        </span>
        <span className="text-xs" style={{ color: DVA_COLORS.border }}>
          ({weight})
        </span>
      </div>
      <div className="flex items-center gap-2">
        <div 
          className="w-16 h-1.5 rounded-full"
          style={{ backgroundColor: DVA_COLORS.border }}
        >
          <div 
            className="h-1.5 rounded-full transition-all duration-300"
            style={{ 
              width: `${score}%`,
              backgroundColor: score >= 75 ? DVA_COLORS.success : 
                              score >= 50 ? DVA_COLORS.warning : DVA_COLORS.orange
            }}
          />
        </div>
        <span 
          className="text-xs font-medium w-8 text-right"
          style={{ color: DVA_COLORS.navy }}
        >
          {score}%
        </span>
      </div>
    </div>
  );
}

// ============================================================
// QUICK STAT ROW
// ============================================================
function QuickStat({ icon: Icon, label, value }) {
  const isEmpty = !value || value === '—';
  
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Icon 
          className="w-4 h-4" 
          style={{ color: isEmpty ? DVA_COLORS.border : DVA_COLORS.slate }} 
        />
        <span 
          className="text-xs"
          style={{ color: DVA_COLORS.slate }}
        >
          {label}
        </span>
      </div>
      <span 
        className="text-sm font-medium"
        style={{ color: isEmpty ? DVA_COLORS.border : DVA_COLORS.navy }}
      >
        {value}
      </span>
    </div>
  );
}

// ============================================================
// LOCAL STRENGTH CALCULATION (fallback if API not available)
// Uses all available field names from the clients table
// ============================================================
export function calculateProfileStrength(profile) {
  if (!profile) {
    return {
      score: 0,
      label: getStrengthLevel(0).label, // 'Minimal' — from the one scale
      color: getStrengthHex(0),
      breakdown: { critical: 0, optional: 0, quality: 0 },
      missingFields: ['Organization Name', 'Organization Type', 'Total Beds']
    };
  }

  // Critical fields (60% weight)
  const criticalFields = [
    { key: 'client_name', alt: 'organization_name', label: 'Organization Name' },
    { key: 'organization_type', label: 'Organization Type' },
    { key: 'beds', alt: 'total_beds', label: 'Total Beds', requirePositive: true },
    { key: 'lives_under_vbc_agreements', alt: 'lives_under_vbc', label: 'Lives Under VBC' },
    { key: 'market_competition_level', label: 'Market Competition Level',
      validate: (v) => ['High', 'Moderate', 'Low', 'high', 'moderate', 'low'].includes(v) },
    { key: 'annual_revenue', label: 'Annual Revenue', requirePositive: true },
    { key: 'ehr_vendor', label: 'EHR Vendor' }
  ];

  // Optional fields (30% weight)
  const optionalFields = [
    { key: 'employed_providers', label: 'Employed Providers' },
    { key: 'affiliates', label: 'Affiliates' },
    { key: 'attributed_lives', label: 'Attributed Lives' },
    { key: 'operating_margin_pct', label: 'Operating Margin' },
    { key: 'rural_percentage', alt: 'rural_pct', label: 'Rural %' },
    { key: 'urban_percentage', alt: 'urban_pct', label: 'Urban %' },
    { key: 'suburban_percentage', alt: 'suburban_pct', label: 'Suburban %' },
    { key: 'payer_mix_medicare_pct', label: 'Medicare %' },
    { key: 'payer_mix_medicaid_pct', label: 'Medicaid %' },
    { key: 'payer_mix_commercial_pct', label: 'Commercial %' }
  ];

  const isFilled = (value, requirePositive = false, validate = null) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    if (requirePositive && (isNaN(Number(value)) || Number(value) <= 0)) return false;
    if (validate && !validate(value)) return false;
    return true;
  };

  // Get value from profile, checking alternate key names
  const getValue = (field) => {
    let value = profile[field.key];
    if (!isFilled(value) && field.alt) {
      value = profile[field.alt];
    }
    return value;
  };

  // Calculate critical score
  let criticalFilled = 0;
  const missingFields = [];
  
  for (const field of criticalFields) {
    const value = getValue(field);
    if (isFilled(value, field.requirePositive, field.validate)) {
      criticalFilled++;
    } else {
      missingFields.push(field.label);
    }
  }
  const criticalScore = Math.round((criticalFilled / criticalFields.length) * 100);

  // Calculate optional score
  let optionalFilled = 0;
  for (const field of optionalFields) {
    const value = getValue(field);
    if (isFilled(value)) {
      optionalFilled++;
    }
  }
  const optionalScore = Math.round((optionalFilled / optionalFields.length) * 100);

  // Quality score
  let qualityPassed = 0;
  const qualityTotal = 4;
  
  // Check beds realistic
  const beds = Number(profile.beds || profile.total_beds);
  if (isNaN(beds) || beds === 0 || (beds >= 10 && beds <= 10000)) {
    qualityPassed++;
  }
  
  // Check VBC lives realistic
  const vbcLives = Number(profile.lives_under_vbc_agreements || profile.lives_under_vbc);
  if (isNaN(vbcLives) || (vbcLives >= 0 && vbcLives <= 10000000)) {
    qualityPassed++;
  }
  
  // Check geography totals ~100%
  const rural = Number(profile.rural_percentage || profile.rural_pct) || 0;
  const urban = Number(profile.urban_percentage || profile.urban_pct) || 0;
  const suburban = Number(profile.suburban_percentage || profile.suburban_pct) || 0;
  const geoTotal = rural + urban + suburban;
  if (geoTotal === 0 || (geoTotal >= 95 && geoTotal <= 105)) {
    qualityPassed++;
  }
  
  // Check payer mix totals ~100%
  const medicare = Number(profile.payer_mix_medicare_pct) || 0;
  const medicaid = Number(profile.payer_mix_medicaid_pct) || 0;
  const commercial = Number(profile.payer_mix_commercial_pct) || 0;
  const selfPay = Number(profile.self_pay_percentage) || 0;
  const payerTotal = medicare + medicaid + commercial + selfPay;
  if (payerTotal === 0 || (payerTotal >= 95 && payerTotal <= 105)) {
    qualityPassed++;
  }
  
  const qualityScore = Math.round((qualityPassed / qualityTotal) * 100);

  // Weighted total: 60% critical, 30% optional, 10% quality
  const total = Math.round(
    (criticalScore * 0.60) +
    (optionalScore * 0.30) +
    (qualityScore * 0.10)
  );

  // Label and colour come from THE shared scale (src/lib/strengthScale) — the page-local
  // >=90/>=75 Good/>=50 Fair/>=25 scale is retired. 100 now reads "Excellent" here and on
  // /home, because both call the same function.
  const label = getStrengthLevel(total).label;
  const color = getStrengthHex(total);

  return {
    score: total,
    label,
    color,
    breakdown: {
      critical: criticalScore,
      optional: optionalScore,
      quality: qualityScore
    },
    missingFields
  };
}

// ============================================================
// HOOK FOR FETCHING PROFILE STRENGTH FROM API
// ============================================================
export function useProfileStrength(clientId) {
  const [strength, setStrength] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clientId) {
      setLoading(false);
      return;
    }

    const fetchStrength = async () => {
      try {
        setLoading(true);
        // P4: use the SAME backend base as the dashboard's strength fetch
        // (EAPersonalizationPage) so the profile-page headline and the
        // dashboard read one source. Previously this used VITE_API_BASE_URL
        // / :3001, so the fetch silently failed and the sidebar fell back to
        // a divergent local field-based calc.
        const response = await apiFetch(`${API_BASE}/api/v2/client-profile/${clientId}/strength`);
        
        if (response.ok) {
          const data = await response.json();
          setStrength(data.strength);
        } else {
          setError('Failed to fetch profile strength');
        }
      } catch (err) {
        console.error('[ProfileStrength] Fetch error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStrength();
  }, [clientId]);

  return { strength, loading, error };
}